import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  CheckCircle,
  Warning,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { contactSchema } from "@/schemas/contact";
import { formatValidationError } from "@/lib/validation-errors";

type TeamSize = "1_10" | "11_50" | "51_200" | "201_500" | "500_plus";
type Topic = "general" | "eap" | "pricing" | "demo" | "other";
type Status = "idle" | "submitting" | "success" | "error";

const TEAM_SIZES: TeamSize[] = ["1_10", "11_50", "51_200", "201_500", "500_plus"];
const TOPICS: Topic[] = ["general", "eap", "pricing", "demo", "other"];

interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  teamSize: TeamSize | "";
  topic: Topic | "";
  message: string;
  website: string; // honeypot
}

const EMPTY: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  teamSize: "",
  topic: "",
  message: "",
  website: "",
};

export function ContactForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    setErrorMessage(null);

    // Ista shema kao na serveru (trimuje), da "Ime: a " ne prođe u browseru a padne na serveru.
    // Honeypot se ne provjerava ovdje, server ga obrađuje tiho.
    if (!form.website) {
      const check = contactSchema.safeParse(form);
      if (!check.success) {
        setErrorMessage(formatValidationError(check.error.issues, form).error);
        setStatus("error");
        return;
      }
    }
    setStatus("submitting");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setErrorMessage(
          res.status === 429
            ? t("kontakt.form.rateLimited")
            : typeof body?.error === "string" && body.error
              ? body.error
              : null,
        );
        setStatus("error");
        return;
      }
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div
        className="rounded-2xl bg-white p-10 sm:p-12"
        style={{
          border: "1px solid var(--color-warm-border)",
          boxShadow: "0 24px 48px -32px oklch(60% 0.05 84 / 0.18)",
        }}
      >
        <div
          className="inline-flex size-12 items-center justify-center rounded-full"
          style={{
            background: "var(--color-brand-bg-light)",
            color: "var(--color-brand-dark)",
          }}
        >
          <CheckCircle weight="duotone" className="size-6" />
        </div>
        <h3 className="font-heading mt-6 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {t("kontakt.form.successTitle")}
        </h3>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          {t("kontakt.form.successBody")}
        </p>
        <Button
          variant="outline"
          className="mt-7 rounded-full"
          onClick={() => setStatus("idle")}
        >
          {t("kontakt.form.successAgain")}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl bg-white p-7 sm:p-9"
      style={{
        border: "1px solid var(--color-warm-border)",
        boxShadow: "0 1px 0 oklch(96% 0.01 84), 0 24px 48px -32px oklch(60% 0.05 84 / 0.16)",
      }}
    >
      <div className="flex items-center justify-between gap-4 border-b pb-5"
        style={{ borderColor: "var(--color-warm-border)" }}
      >
        <div>
          <span
            className="text-[10px] font-semibold tracking-[0.22em] uppercase"
            style={{ color: "var(--color-section-label)" }}
          >
            {t("kontakt.form.sectionTitle")}
          </span>
          <h3 className="font-heading mt-1 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
            {t("kontakt.hero.title")}{" "}
            <em
              className="not-italic"
              style={{ color: "var(--color-brand-dark)" }}
            >
              {t("kontakt.hero.titleHighlight")}
            </em>
            .
          </h3>
        </div>
        <PaperPlaneTilt
          weight="duotone"
          className="hidden size-8 sm:block"
          style={{ color: "var(--color-brand-dark)" }}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field
          label={t("kontakt.form.nameLabel")}
          required
        >
          <Input
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder={t("kontakt.form.namePlaceholder")}
            required
            minLength={2}
            maxLength={120}
            autoComplete="name"
            className="h-11"
          />
        </Field>

        <Field
          label={t("kontakt.form.emailLabel")}
          required
        >
          <Input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder={t("kontakt.form.emailPlaceholder")}
            required
            maxLength={200}
            autoComplete="email"
            className="h-11"
          />
        </Field>

        <Field
          label={t("kontakt.form.companyLabel")}
          required
        >
          <Input
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            placeholder={t("kontakt.form.companyPlaceholder")}
            required
            maxLength={160}
            autoComplete="organization"
            className="h-11"
          />
        </Field>

        <Field
          label={t("kontakt.form.phoneLabel")}
          hint={t("kontakt.form.phoneOptional")}
        >
          <Input
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder={t("kontakt.form.phonePlaceholder")}
            maxLength={40}
            autoComplete="tel"
            className="h-11"
          />
        </Field>

        <Field
          label={t("kontakt.form.teamSizeLabel")}
          required
        >
          <Select
            value={form.teamSize}
            onValueChange={(v) => update("teamSize", v as TeamSize)}
            required
          >
            <SelectTrigger className="h-11 w-full">
              <SelectValue
                placeholder={t("kontakt.form.teamSizePlaceholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {TEAM_SIZES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`kontakt.form.teamSize_${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field
          label={t("kontakt.form.topicLabel")}
          required
        >
          <Select
            value={form.topic}
            onValueChange={(v) => update("topic", v as Topic)}
            required
          >
            <SelectTrigger className="h-11 w-full">
              <SelectValue placeholder={t("kontakt.form.topicPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {TOPICS.map((tp) => (
                <SelectItem key={tp} value={tp}>
                  {t(`kontakt.form.topic_${tp}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="sm:col-span-2">
          <Field
            label={t("kontakt.form.messageLabel")}
            required
          >
            <Textarea
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder={t("kontakt.form.messagePlaceholder")}
              required
              minLength={10}
              maxLength={4000}
              rows={5}
              className="min-h-32 resize-y"
            />
          </Field>
        </div>
      </div>

      {/* Honeypot — hidden from humans, visible to naive bots */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => update("website", e.target.value)}
          />
        </label>
      </div>

      {status === "error" && (
        <div
          className="mt-6 flex items-start gap-3 rounded-lg border p-4"
          style={{
            borderColor: "oklch(80% 0.08 27)",
            background: "oklch(97% 0.02 27)",
          }}
        >
          <Warning
            weight="duotone"
            className="mt-0.5 size-5 shrink-0"
            style={{ color: "oklch(50% 0.18 27)" }}
          />
          <div className="text-sm leading-relaxed">
            <div
              className="font-semibold"
              style={{ color: "oklch(40% 0.16 27)" }}
            >
              {t("kontakt.form.errorTitle")}
            </div>
            <div className="mt-0.5 text-foreground/80">
              {errorMessage ?? t("kontakt.form.errorBody")}
            </div>
          </div>
        </div>
      )}

      <p className="mt-7 text-xs leading-relaxed text-muted-foreground">
        {t("kontakt.form.consent")}
      </p>

      <div className="mt-5 flex items-center justify-end">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="group inline-flex h-12 items-center justify-center gap-2.5 rounded-full bg-primary px-9 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
          style={{
            boxShadow: "0 4px 24px var(--color-brand-shadow)",
          }}
        >
          {status === "submitting"
            ? t("kontakt.form.submitting")
            : t("kontakt.form.submit")}
          <ArrowRight
            className="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
            weight="bold"
          />
        </button>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, required, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-[13px] font-medium text-foreground">
        {label}
        {required && (
          <span
            aria-hidden
            className="ml-0.5"
            style={{ color: "var(--color-brand-dark)" }}
          >
            *
          </span>
        )}
        {hint && (
          <span className="ml-1.5 text-[11px] font-normal text-muted-foreground/70">
            {hint}
          </span>
        )}
      </Label>
      {children}
    </div>
  );
}
